import { useState, useEffect, useRef } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useAuthStore } from '../../stores/authStore'
import { 
  BookOpenIcon, 
  CalendarIcon, 
  UserGroupIcon,
  CurrencyDollarIcon,
  ChartBarIcon,
  ClockIcon,
  ExclamationCircleIcon,
  BellIcon,
  EyeIcon,
  StarIcon,
  ArrowTrendingUpIcon,
  CheckCircleIcon,
  XCircleIcon,
  GlobeAltIcon,
  DocumentTextIcon,
  ChevronRightIcon
} from '@heroicons/react/24/outline'

// 收入趋势图组件 (SVG)
const RevenueChart = ({ data }: { data: number[] }) => {
  const max = Math.max(...data, 100);
  const width = 100;
  const height = 50;
  
  const points = data.map((val, i) => {
    const x = (i / (data.length - 1)) * width;
    const y = height - (val / max) * height;
    return `${x},${y}`;
  }).join(' ');

  return (
    <div className="relative h-24 w-full overflow-hidden">
      <svg viewBox={`0 0 ${width} ${height}`} preserveAspectRatio="none" className="w-full h-full">
        <defs>
          <linearGradient id="revenue-gradient" x1="0" x2="0" y1="0" y2="1">
            <stop offset="0%" stopColor="#4F46E5" stopOpacity="0.2" />
            <stop offset="100%" stopColor="#4F46E5" stopOpacity="0" />
          </linearGradient>
        </defs>
        <path d={`M0,${height} L${points} L${width},${height} Z`} fill="url(#revenue-gradient)" />
        <polyline fill="none" stroke="#4F46E5" strokeWidth="2" points={points} vectorEffect="non-scaling-stroke" />
      </svg>
    </div>
  );
};

// 教学日历微件
const TeachingCalendar = ({ bookings }: { bookings?: any[] }) => {
  const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  const today = new Date().getDate();
  const currentMonth = new Date().getMonth();
  const currentYear = new Date().getFullYear();
  
  // 从真实预约数据计算课程分布
  const courseDistribution = Array.from({ length: 31 }, (_, i) => {
    const day = i + 1;
    if (!bookings || bookings.length === 0) return 0;
    
    // 统计当天的预约数量
    const count = bookings.filter(booking => {
      const bookingDate = new Date(booking.date);
      return bookingDate.getDate() === day && 
             bookingDate.getMonth() === currentMonth && 
             bookingDate.getFullYear() === currentYear &&
             (booking.status === 'confirmed' || booking.status === 'completed');
    }).length;
    
    return count;
  });

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-6 h-full">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-bold text-gray-900">本月教学日历</h3>
        <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded">2024年5月</span>
      </div>
      <div className="grid grid-cols-7 gap-1 mb-2">
        {days.map(d => (
          <div key={d} className="text-center text-xs text-gray-400 font-medium">{d}</div>
        ))}
      </div>
      <div className="grid grid-cols-7 gap-2">
        {/* 简单的日期网格 */}
        {Array.from({ length: 31 }).map((_, i) => {
          const day = i + 1;
          const count = courseDistribution[i];
          const isToday = day === today;
          
          return (
            <div 
              key={i} 
              className={`
                aspect-square rounded-lg flex flex-col items-center justify-center text-xs cursor-pointer transition-all
                ${isToday ? 'border-2 border-indigo-500 font-bold' : 'border border-transparent'}
                ${count > 0 ? 'bg-indigo-50 text-indigo-700 hover:bg-indigo-100' : 'bg-gray-50 text-gray-400 hover:bg-gray-100'}
              `}
            >
              <span>{day}</span>
              {count > 0 && (
                <div className="flex gap-0.5 mt-0.5">
                  {Array.from({ length: Math.min(count, 3) }).map((_, idx) => (
                    <div key={idx} className="w-1 h-1 rounded-full bg-indigo-500"></div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

// 扩展 Booking 接口
interface Booking {
  id: string
  date: string
  startTime: string
  endTime: string
  status: string
  student: {
    firstName: string
    lastName: string
    email: string
    targetSchool?: string
    applicationStage?: string
    timezone?: string
    avatar?: string
  }
  course: {
    title: string
  }
  createdAt: string
  notes?: string
}

interface TodoItem {
  id: string
  type: 'review_essay' | 'mock_interview' | 'confirm_booking'
  title: string
  deadline: string
  priority: 'high' | 'medium' | 'low'
  completed?: boolean
}

interface TeacherStats {
  activeStudents: number
  totalLessons: number
  totalRevenue: number
  pendingBookings: number
  confirmedBookings: number
  averageRating: number
  revenueData: number[]
}

const TeacherDashboard = () => {
  const { user } = useAuthStore()
  const navigate = useNavigate()
  
  const [stats, setStats] = useState<TeacherStats>({
    activeStudents: 0,
    totalLessons: 0,
    totalRevenue: 0,
    pendingBookings: 0,
    confirmedBookings: 0,
    averageRating: 0,
    revenueData: [0, 0, 0, 0, 0, 0]
  })

  const [todos, setTodos] = useState<TodoItem[]>([])
  const [recentBookings, setRecentBookings] = useState<Booking[]>([])
  const [allBookings, setAllBookings] = useState<any[]>([])
  const [loading, setLoading] = useState(true)

  // 加载真实数据
  useEffect(() => {
    fetchDashboardData()
  }, [])

  const fetchDashboardData = async () => {
    try {
      const token = localStorage.getItem('token')
      if (!token) {
        setLoading(false)
        return
      }

      const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }

      // 并行请求数据
      const [bookingsRes, coursesRes] = await Promise.allSettled([
        fetch('/api/bookings/teacher', { headers }),
        fetch('/api/courses/teacher/my-courses', { headers })
      ])

      // 处理预约数据
      if (bookingsRes.status === 'fulfilled' && bookingsRes.value.ok) {
        const bookings = await bookingsRes.value.json()
        const bookingsList = Array.isArray(bookings) ? bookings : []
        
        // 保存所有预约数据用于日历显示
        setAllBookings(bookingsList)
        
        // 设置近期预约
        const recent = bookingsList.slice(0, 5).map((b: any) => ({
          id: b.id,
          date: b.date,
          startTime: b.startTime,
          endTime: b.endTime,
          status: b.status,
          student: {
            firstName: b.student?.firstName || '',
            lastName: b.student?.lastName || '',
            email: b.student?.email || '',
            avatar: b.student?.avatar
          },
          course: { title: b.course?.title || b.course?.subject || '课程' },
          createdAt: b.createdAt
        }))
        setRecentBookings(recent)

        // 计算统计数据
        const pendingCount = bookingsList.filter((b: any) => b.status === 'pending').length
        const confirmedCount = bookingsList.filter((b: any) => b.status === 'confirmed').length
        const completedBookings = bookingsList.filter((b: any) => b.status === 'completed')
        const totalRevenue = completedBookings.reduce((sum: number, b: any) => sum + (b.totalPrice || 0), 0)
        const uniqueStudents = new Set(bookingsList.map((b: any) => b.studentId)).size

        // 生成待办事项
        const newTodos: TodoItem[] = bookingsList
          .filter((b: any) => b.status === 'pending')
          .slice(0, 3)
          .map((b: any, i: number) => ({
            id: b.id,
            type: 'confirm_booking' as const,
            title: `确认 ${b.student?.firstName || '学生'}${b.student?.lastName || ''}的预约`,
            deadline: new Date(b.date).toLocaleDateString('zh-CN'),
            priority: i === 0 ? 'high' as const : 'medium' as const
          }))
        setTodos(newTodos.length > 0 ? newTodos : [])

        setStats(prev => ({
          ...prev,
          activeStudents: uniqueStudents,
          totalLessons: completedBookings.length,
          totalRevenue,
          pendingBookings: pendingCount,
          confirmedBookings: confirmedCount
        }))
      } else {
        setRecentBookings([])
        setTodos([])
      }

      // 处理课程数据获取评分
      if (coursesRes.status === 'fulfilled' && coursesRes.value.ok) {
        const coursesData = await coursesRes.value.json()
        const courses = coursesData.courses || coursesData || []
        if (courses.length > 0) {
          const ratings = courses.filter((c: any) => c.rating).map((c: any) => parseFloat(c.rating) || 0)
          const avgRating = ratings.length > 0 ? ratings.reduce((a: number, b: number) => a + b, 0) / ratings.length : 0
          setStats(prev => ({ ...prev, averageRating: avgRating }))
        }
      }

    } catch (error) {
      console.error('获取仪表板数据失败:', error)
      setRecentBookings([])
      setTodos([])
    } finally {
      setLoading(false)
    }
  }

  // 收入数据从状态中获取
  const revenueData = stats.revenueData.some(v => v > 0) ? stats.revenueData : [0, 0, 0, 0, 0, stats.totalRevenue]

  const toggleTodo = (id: string) => {
    setTodos(todos.map(t => t.id === id ? { ...t, completed: !t.completed } : t))
  }

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto py-8 px-4 flex justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    )
  }

  return (
    <div className="max-w-7xl mx-auto py-8 px-4">
      {/* 欢迎栏 */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center">
            工作台
            <span className="ml-3 text-sm font-normal text-white bg-gradient-to-r from-indigo-500 to-purple-600 px-3 py-1 rounded-full shadow-sm">
              金牌导师
            </span>
          </h1>
          <p className="text-gray-500 mt-1">今天是 {new Date().toLocaleDateString('zh-CN', { month: 'long', day: 'numeric', weekday: 'long' })}，祝您教学愉快！</p>
        </div>
        <div className="flex gap-3">
          <button 
            onClick={() => navigate('/teacher/create-course')}
            className="flex items-center px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors shadow-sm"
          >
            <BookOpenIcon className="w-5 h-5 mr-2" />
            发布课程
          </button>
          <button 
            onClick={() => navigate('/teacher/schedule')}
            className="flex items-center px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
          >
            <CalendarIcon className="w-5 h-5 mr-2" />
            日程管理
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-8">
        {/* 左侧：数据概览与收入 */}
        <div className="lg:col-span-2 space-y-8">
          {/* 核心指标卡片 */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm relative overflow-hidden group">
              <div className="absolute right-0 top-0 p-4 opacity-10 group-hover:opacity-20 transition-opacity">
                <UserGroupIcon className="w-24 h-24 text-indigo-600" />
              </div>
              <p className="text-sm text-gray-500 font-medium">活跃学生</p>
              <p className="text-3xl font-bold text-gray-900 mt-2">{stats.activeStudents}</p>
              <p className="text-xs text-green-600 mt-2 flex items-center">
                <ArrowTrendingUpIcon className="w-3 h-3 mr-1" />
                {stats.pendingBookings > 0 ? `${stats.pendingBookings} 待确认` : '暂无待处理'}
              </p>
            </div>
            
            <div className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm relative overflow-hidden group">
              <div className="absolute right-0 top-0 p-4 opacity-10 group-hover:opacity-20 transition-opacity">
                <BookOpenIcon className="w-24 h-24 text-blue-600" />
              </div>
              <p className="text-sm text-gray-500 font-medium">完成课时</p>
              <p className="text-3xl font-bold text-gray-900 mt-2">{stats.totalLessons}</p>
              <p className="text-xs text-blue-600 mt-2">{stats.confirmedBookings} 个已确认预约</p>
            </div>

            <div className="bg-gradient-to-br from-indigo-600 to-purple-700 p-6 rounded-xl shadow-lg text-white relative overflow-hidden">
              <div className="absolute right-0 top-0 p-4 opacity-20">
                <CurrencyDollarIcon className="w-24 h-24 text-white" />
              </div>
              <p className="text-sm text-indigo-100 font-medium">累计收入</p>
              <p className="text-3xl font-bold mt-2">¥{stats.totalRevenue.toLocaleString()}</p>
              <div className="mt-4 h-12 opacity-80">
                <RevenueChart data={revenueData} />
              </div>
            </div>
          </div>

          {/* 待办事项 - 交互升级 */}
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-100 bg-gray-50 flex justify-between items-center">
              <h3 className="font-bold text-gray-900 flex items-center">
                <CheckCircleIcon className="w-5 h-5 text-indigo-600 mr-2" />
                教学待办
              </h3>
              <span className="text-xs font-medium bg-white px-2 py-1 rounded-full border border-gray-200 text-gray-500">
                {todos.filter(t => !t.completed).length} 待处理
              </span>
            </div>
            <div className="divide-y divide-gray-50">
              {todos.map(todo => (
                <div 
                  key={todo.id} 
                  className={`p-4 flex items-center justify-between transition-all ${
                    todo.completed ? 'bg-gray-50 opacity-60' : 'hover:bg-indigo-50/30'
                  }`}
                >
                  <div className="flex items-center gap-4">
                    <button 
                      onClick={() => toggleTodo(todo.id)}
                      className={`w-6 h-6 rounded-full border-2 flex items-center justify-center transition-colors ${
                        todo.completed ? 'bg-indigo-600 border-indigo-600' : 'border-gray-300 hover:border-indigo-400'
                      }`}
                    >
                      {todo.completed && <CheckCircleIcon className="w-4 h-4 text-white" />}
                    </button>
                    <div>
                      <p className={`text-sm font-medium ${todo.completed ? 'text-gray-500 line-through' : 'text-gray-900'}`}>
                        {todo.title}
                      </p>
                      <p className="text-xs text-gray-500 mt-0.5 flex items-center">
                        <ClockIcon className="w-3 h-3 mr-1" />
                        截止: {todo.deadline}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    {!todo.completed && todo.priority === 'high' && (
                      <span className="px-2 py-1 text-xs font-medium text-red-700 bg-red-50 rounded-full">紧急</span>
                    )}
                    <button className="text-gray-400 hover:text-indigo-600">
                      <ChevronRightIcon className="w-5 h-5" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* 近期预约 - 档案透视版 */}
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-100 flex justify-between items-center">
              <h3 className="font-bold text-gray-900">近期课程</h3>
              <Link to="/teacher/bookings" className="text-sm text-indigo-600 hover:text-indigo-700 font-medium">
                全部预约 &rarr;
              </Link>
            </div>
            <div className="divide-y divide-gray-50">
              {recentBookings.map((booking) => (
                <div key={booking.id} className="p-5 hover:bg-gray-50 transition-colors group">
                  <div className="flex justify-between items-start">
                    <div className="flex gap-4">
                      <div className="flex-shrink-0">
                        <div className="w-12 h-12 rounded-full bg-indigo-100 flex items-center justify-center text-indigo-600 font-bold text-lg">
                          {booking.student.lastName[0]}
                        </div>
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <h4 className="font-bold text-gray-900">{booking.student.firstName} {booking.student.lastName}</h4>
                          <span className="px-2 py-0.5 bg-purple-50 text-purple-700 text-xs rounded font-medium border border-purple-100">
                            {booking.student.targetSchool || '目标未定'}
                          </span>
                        </div>
                        <p className="text-sm text-gray-600 mt-1">{booking.course.title}</p>
                        <div className="flex items-center gap-4 mt-2 text-xs text-gray-500">
                          <span className="flex items-center">
                            <CalendarIcon className="w-3 h-3 mr-1" />
                            {booking.date}
                          </span>
                          <span className="flex items-center">
                            <ClockIcon className="w-3 h-3 mr-1" />
                            {booking.startTime}-{booking.endTime}
                          </span>
                          <span className="flex items-center text-orange-600 bg-orange-50 px-1.5 rounded">
                            <GlobeAltIcon className="w-3 h-3 mr-1" />
                            {booking.student.timezone}
                          </span>
                        </div>
                      </div>
                    </div>
                    <div>
                      {booking.status === 'pending' ? (
                        <div className="flex gap-2">
                          <button className="px-3 py-1.5 bg-indigo-600 text-white text-xs font-medium rounded-lg hover:bg-indigo-700 transition-colors shadow-sm">
                            确认
                          </button>
                          <button className="px-3 py-1.5 bg-white border border-gray-300 text-gray-700 text-xs font-medium rounded-lg hover:bg-gray-50">
                            拒绝
                          </button>
                        </div>
                      ) : (
                        <span className="px-3 py-1 bg-green-50 text-green-700 text-xs font-medium rounded-full">
                          已确认
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* 右侧：日历与辅助信息 */}
        <div className="lg:col-span-1 space-y-8">
          <TeachingCalendar bookings={allBookings} />
          
          {/* 快捷入口 */}
          <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
            <h3 className="font-bold text-gray-900 mb-4">常用功能</h3>
            <div className="grid grid-cols-2 gap-3">
              <button 
                onClick={() => navigate('/teacher/students')}
                className="flex flex-col items-center p-4 bg-gray-50 rounded-xl hover:bg-indigo-50 hover:text-indigo-600 transition-all group"
              >
                <UserGroupIcon className="w-6 h-6 text-gray-400 group-hover:text-indigo-600 mb-2" />
                <span className="text-xs font-medium">学生档案</span>
              </button>
              <button className="flex flex-col items-center p-4 bg-gray-50 rounded-xl hover:bg-indigo-50 hover:text-indigo-600 transition-all group">
                <DocumentTextIcon className="w-6 h-6 text-gray-400 group-hover:text-indigo-600 mb-2" />
                <span className="text-xs font-medium">教案库</span>
              </button>
              <button className="flex flex-col items-center p-4 bg-gray-50 rounded-xl hover:bg-indigo-50 hover:text-indigo-600 transition-all group">
                <StarIcon className="w-6 h-6 text-gray-400 group-hover:text-indigo-600 mb-2" />
                <span className="text-xs font-medium">评价管理</span>
              </button>
              <button className="flex flex-col items-center p-4 bg-gray-50 rounded-xl hover:bg-indigo-50 hover:text-indigo-600 transition-all group">
                <BellIcon className="w-6 h-6 text-gray-400 group-hover:text-indigo-600 mb-2" />
                <span className="text-xs font-medium">消息通知</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default TeacherDashboard
